import React, { useEffect, useMemo, useState } from "react";
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
} from "firebase/storage";
import { FiImage, FiRefreshCw, FiSave, FiTrash2, FiUpload } from "react-icons/fi";

import PermissionGuard from "../components/PermissionGuard";
import { db, storage } from "../firebase";

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  page: "#f5f6f8",
  primary: "#f20c53",
  secondary: "#fb6c33",
  warn: "#b91c1c",
};

const DEFAULT_FORM = Object.freeze({
  titleLine1: "Book Top Talents",
  titleLine2: "Near You",
  subtitle: "Experience the best live performances!",
  primaryLabel: "Artists",
  secondaryLabel: "Mentors",
  imageUrl: "",
  storagePath: "",
});

export default function AppHomeBanner() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [toast, setToast] = useState("");

  const canManage = useMemo(() => {
    try {
      const raw = localStorage.getItem("employee");
      const employee = raw ? JSON.parse(raw) : {};
      if (employee?.role === "super_admin") return true;
      const perms = Array.isArray(employee?.permissions) ? employee.permissions : [];
      return perms.includes("manage_event_app_banners");
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "app_home_banner", "main"),
      (snapshot) => {
        setForm({
          ...DEFAULT_FORM,
          ...(snapshot.data() || {}),
        });
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setToast("Failed to load home banner settings.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const previewUrl = useMemo(() => {
    if (!selectedFile) return form.imageUrl || "";
    return URL.createObjectURL(selectedFile);
  }, [selectedFile, form.imageUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  function setField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSave() {
    if (!canManage) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "app_home_banner", "main"),
        {
          ...form,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setToast("Home banner content saved.");
    } catch (error) {
      console.error(error);
      setToast("Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload() {
    if (!selectedFile || !canManage) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      const blob = await compressToWebP(selectedFile, {
        maxW: 1920,
        maxH: 1080,
        quality: 0.84,
      });
      const path = "app_home_banner/main.webp";
      const ref = storageRef(storage, path);
      const task = uploadBytesResumable(ref, blob, {
        contentType: "image/webp",
        cacheControl: "public,max-age=31536000,immutable",
      });

      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snapshot) => {
            if (!snapshot.totalBytes) return;
            setUploadProgress(
              Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            );
          },
          reject,
          resolve
        );
      });

      const imageUrl = await getDownloadURL(ref);
      const next = { ...form, imageUrl, storagePath: path };
      setForm(next);
      await setDoc(
        doc(db, "app_home_banner", "main"),
        {
          ...next,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSelectedFile(null);
      setToast("Banner image updated.");
    } catch (error) {
      console.error(error);
      setToast("Upload failed.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }

  async function handleRemoveImage() {
    if (!canManage) return;

    try {
      if (form.storagePath) {
        await deleteObject(storageRef(storage, form.storagePath));
      }
    } catch (error) {
      console.warn("Image delete skipped:", error);
    }

    try {
      const next = { ...form, imageUrl: "", storagePath: "" };
      setForm(next);
      await setDoc(
        doc(db, "app_home_banner", "main"),
        {
          ...next,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSelectedFile(null);
      setToast("Banner image removed.");
    } catch (error) {
      console.error(error);
      setToast("Could not remove image.");
    }
  }

  return (
    <PermissionGuard requiredPermission={["view_event_app_banners", "manage_event_app_banners"]}>
      <div style={{ minHeight: "100vh", background: COLORS.page, padding: 16, fontFamily: "Inter, system-ui, sans-serif" }}>
        <div style={hero}>
          <div>
            <h2 style={{ margin: 0, color: COLORS.text, fontWeight: 900 }}>App Home Banner</h2>
            <p style={{ margin: "6px 0 0", color: COLORS.subtext }}>
              Manage the home-page hero banner content and image used in the mobile app.
            </p>
          </div>
          <div style={heroStats}>
            <Stat label="Document" value="app_home_banner/main" />
            <Stat label="Image Path" value="app_home_banner/main.webp" />
          </div>
        </div>

        <div style={gridWrap}>
          <section style={panel}>
            <div style={sectionTop}>
              <div>
                <h3 style={sectionTitle}>Banner Content</h3>
                <p style={sectionCopy}>These fields feed the app hero card directly.</p>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...DEFAULT_FORM, imageUrl: form.imageUrl, storagePath: form.storagePath })}
                style={ghostBtn}
                disabled={!canManage}
              >
                <FiRefreshCw /> Reset Text
              </button>
            </div>

            {loading ? (
              <div style={{ color: COLORS.subtext }}>Loading...</div>
            ) : (
              <>
                <Field label="Top line">
                  <input
                    value={form.titleLine1}
                    onChange={(event) => setField("titleLine1", event.target.value)}
                    style={input}
                    disabled={!canManage}
                  />
                </Field>
                <Field label="Second line">
                  <input
                    value={form.titleLine2}
                    onChange={(event) => setField("titleLine2", event.target.value)}
                    style={input}
                    disabled={!canManage}
                  />
                </Field>
                <Field label="Subtitle">
                  <textarea
                    value={form.subtitle}
                    onChange={(event) => setField("subtitle", event.target.value)}
                    style={{ ...input, minHeight: 88, resize: "vertical" }}
                    disabled={!canManage}
                  />
                </Field>
                <div style={twoCol}>
                  <Field label="Primary button">
                    <input
                      value={form.primaryLabel}
                      onChange={(event) => setField("primaryLabel", event.target.value)}
                      style={input}
                      disabled={!canManage}
                    />
                  </Field>
                  <Field label="Secondary button">
                    <input
                      value={form.secondaryLabel}
                      onChange={(event) => setField("secondaryLabel", event.target.value)}
                      style={input}
                      disabled={!canManage}
                    />
                  </Field>
                </div>
              </>
            )}
          </section>
          <section style={panel}>
            <div style={sectionTop}>
              <div>
                <h3 style={sectionTitle}>Banner Image</h3>
                <p style={sectionCopy}>Upload the hero artwork that matches the mobile home banner.</p>
              </div>
            </div>

            <div style={previewShell}>
              <div style={bannerPreview(previewUrl)}>
                <div style={bannerOverlay}>
                  <div style={liveBadge}>Live Bookings</div>
                  <h4 style={previewTitle}>{form.titleLine1}</h4>
                  <h4 style={{ ...previewTitle, marginTop: -6 }}>{form.titleLine2}</h4>
                  <p style={previewSubtitle}>{form.subtitle}</p>
                  <div style={buttonRow}>
                    <span style={previewPrimaryBtn}>{form.primaryLabel || "Artists"}</span>
                    <span style={previewGhostBtn}>{form.secondaryLabel || "Mentors"}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <label style={uploadBox}>
                <FiImage size={18} />
                <span>{selectedFile ? selectedFile.name : "Choose banner image"}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                  style={{ display: "none" }}
                  disabled={!canManage || uploading}
                />
              </label>

              <div style={{ fontSize: 12, color: COLORS.subtext }}>
                Tip: upload a high-quality wide banner with the subject on the right half. It is compressed to WebP before saving.
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleUpload}
                  style={primaryBtn}
                  disabled={!selectedFile || !canManage || uploading}
                >
                  <FiUpload /> {uploading ? `Uploading ${uploadProgress}%` : "Upload Image"}
                </button>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  style={dangerBtn}
                  disabled={!form.imageUrl || !canManage || uploading}
                >
                  <FiTrash2 /> Remove Image
                </button>
              </div>
            </div>
          </section>
        </div>

        <div style={footerBar}>
          <button
            type="button"
            onClick={handleSave}
            style={primaryBtn}
            disabled={!canManage || saving || loading}
          >
            <FiSave /> {saving ? "Saving..." : "Save Content"}
          </button>
          {!canManage && (
            <span style={{ color: COLORS.warn, fontWeight: 700 }}>
              View only: manage permission is required to edit.
            </span>
          )}
        </div>

        {toast && <div style={toastStyle}>{toast}</div>}
      </div>
    </PermissionGuard>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "grid", gap: 8 }}>
      <span style={{ color: COLORS.text, fontWeight: 800, fontSize: 13 }}>{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div style={statCard}>
      <div style={{ color: COLORS.subtext, fontSize: 12, fontWeight: 700 }}>{label}</div>
      <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 900 }}>{value}</div>
    </div>
  );
}

async function compressToWebP(file, { maxW = 1920, maxH = 1080, quality = 0.84 } = {}) {
  const src = URL.createObjectURL(file);
  try {
    const image = await loadImage(src);
    const scale = Math.min(maxW / image.width, maxH / image.height, 1);
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, width, height);
    const blob = await new Promise((resolve) =>
      canvas.toBlob((value) => resolve(value), "image/webp", quality)
    );
    if (!blob) throw new Error("WEBP export failed");
    return blob;
  } finally {
    URL.revokeObjectURL(src);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const heroStats = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  minWidth: 260,
};

const statCard = {
  background: "#fff8fb",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: "10px 12px",
  display: "grid",
  gap: 4,
};

const gridWrap = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: 16,
  marginTop: 16,
};

const panel = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
  display: "grid",
  gap: 16,
};

const sectionTop = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
};

const sectionTitle = {
  margin: 0,
  color: COLORS.text,
  fontWeight: 900,
  fontSize: 18,
};

const sectionCopy = {
  margin: "6px 0 0",
  color: COLORS.subtext,
  fontSize: 13,
};

const twoCol = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 12,
};

const input = {
  width: "100%",
  borderRadius: 14,
  border: `1px solid ${COLORS.border}`,
  padding: "12px 14px",
  fontSize: 14,
  color: COLORS.text,
  outline: "none",
  boxSizing: "border-box",
};

const uploadBox = {
  border: `1px dashed ${COLORS.border}`,
  borderRadius: 14,
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: COLORS.text,
  fontWeight: 700,
  cursor: "pointer",
  background: "#fff8fb",
};

const primaryBtn = {
  border: "none",
  borderRadius: 14,
  padding: "11px 16px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: "linear-gradient(135deg, #f20c53 0%, #fb6c33 100%)",
};

const ghostBtn = {
  borderRadius: 14,
  border: `1px solid ${COLORS.border}`,
  padding: "10px 14px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  color: COLORS.text,
  fontWeight: 700,
  cursor: "pointer",
  background: "#fff",
};

const dangerBtn = {
  ...ghostBtn,
  color: COLORS.warn,
};

const previewShell = {
  background: "#fff8fb",
  borderRadius: 20,
  padding: 14,
  border: `1px solid ${COLORS.border}`,
};

const bannerPreview = (previewUrl) => ({
  minHeight: 198,
  borderRadius: 24,
  overflow: "hidden",
  position: "relative",
  display: "flex",
  alignItems: "stretch",
  background: previewUrl
    ? `linear-gradient(90deg, rgba(24,17,34,0.94) 0%, rgba(60,24,49,0.8) 48%, rgba(0,0,0,0.12) 68%, rgba(0,0,0,0.04) 100%), url(${previewUrl}) right center/cover`
    : "linear-gradient(135deg, #241722 0%, #5b2140 50%, #9f4f5d 100%)",
  boxShadow: "0 16px 32px rgba(15, 23, 42, 0.12)",
});

const bannerOverlay = {
  width: "50%",
  minWidth: 0,
  padding: "16px 18px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: 198,
  boxSizing: "border-box",
};

const liveBadge = {
  display: "inline-flex",
  width: "fit-content",
  padding: "5px 10px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.16)",
  color: "#fff",
  fontSize: 11,
  fontWeight: 800,
  border: "1px solid rgba(255,255,255,0.16)",
};

const previewTitle = {
  margin: 0,
  color: "#fff",
  fontSize: 30,
  fontWeight: 900,
  lineHeight: 0.95,
  maxWidth: "100%",
};

const previewSubtitle = {
  margin: "7px 0 0",
  color: "rgba(255,255,255,0.9)",
  fontSize: 12.5,
  fontWeight: 500,
  maxWidth: "100%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const buttonRow = {
  display: "flex",
  gap: 8,
  flexWrap: "nowrap",
  marginTop: 12,
};

const previewPrimaryBtn = {
  flex: 1,
  minWidth: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "9px 12px",
  borderRadius: 14,
  color: "#fff",
  fontWeight: 800,
  background: "linear-gradient(135deg, #f20c53 0%, #fb6c33 100%)",
};

const previewGhostBtn = {
  flex: 1,
  minWidth: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "9px 12px",
  borderRadius: 14,
  color: "#fff",
  fontWeight: 800,
  background: "rgba(255,255,255,0.16)",
  border: "1px solid rgba(255,255,255,0.28)",
};

const footerBar = {
  marginTop: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
};

const toastStyle = {
  position: "fixed",
  right: 18,
  bottom: 18,
  background: "#111827",
  color: "#fff",
  borderRadius: 14,
  padding: "12px 16px",
  fontWeight: 800,
  boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
};
